// src/features/pages/admin/DelegationsPage.tsx
// Org-scope delegation — a temporary capability overlay. "While I'm away, let
// someone act on my behalf for exactly these things, over exactly this
// population, between these dates." A delegate never gains more than the
// delegator holds: the capability list is filtered to what YOU hold, the scope
// is intersected with your live population by the backend, and sensitive
// capabilities demand a reason (and may need re-authentication / admin approval).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { UserCheck, X, ShieldAlert, Clock, Info } from 'lucide-react';
import { attachCookieAuth } from '@/lib/session';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { DateTimeField } from '@/features/shared/components/DateTimeField';
import { Badge } from '@/features/shared/components/ui/badge';
import { delegationCatalog, delegationPolicy } from '@/rbac/rbac.generated';
import { usePermissionChecker, useHasPermission } from '@/rbac/useRbac';
import { getDepartments, listBranches, replaceLeaveDelegation } from '@/lib/hrApi';
import { ReauthDialog } from '@/features/shared/components/ReauthDialog';
import { getReauthStatus, isReauthRequiredError } from '@/services/reauthService';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { DataState } from '@/features/shared/components/ui/DataState';
import { DelegateInbox } from '@/features/delegation/DelegateInbox';
import { DelegationDetailSheet, type DetailState } from '@/features/delegation/DelegationDetailSheet';
import { DelegationNotes } from '@/features/delegation/DelegationNotes';
import { DELEGATION_ID_PARAM, REPLACE_FOR_PARAM, delegationStatusLabel, firstName } from '@/features/delegation/handover';
import { appRoute, delegationWindowFromLeave, type FromLeaveContext } from '@/features/delegation/leaveBridge';
import { CalendarCheck, ArrowRight } from 'lucide-react';
import {
  getDelegations,
  createDelegation,
  revokeDelegation,
  getPendingDelegations,
  approveDelegation,
  rejectDelegation,
  acceptDelegation,
  declineDelegation,
  getDelegationPolicy,
  type DelegationPolicy,
  type Delegation,
  type MyDelegations,
  type DelegationScopeType,
} from '@/services/rbacService';

const AUTH_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const api = (() => {
  const i = axios.create({ baseURL: AUTH_BASE, timeout: 30000, withCredentials: true });
  i.interceptors.request.use(attachCookieAuth);
  return i;
})();

interface EmpHit {
  employeeId: string;
  userId: string | null;
  name: string;
  sublabel?: string;
}

async function searchDelegates(q: string): Promise<EmpHit[]> {
  const term = q.trim();
  if (term.length < 1) return [];
  const { data } = await api.get<any[]>('/api/v1/employees/search', { params: { q: term, limit: 10 } });
  // `id` is the employee RECORD id (UUID) the delegation stores and the
  // population resolver matches on; `employee_id` is the human employee number
  // (e.g. "AMF-001") — display only, never an identifier here.
  return (data || [])
    .filter((e) => !!e?.id)
    .map((e) => ({
      employeeId: String(e.id),
      userId: e.user_id ? String(e.user_id) : null,
      name: [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || String(e.employee_id ?? e.id),
      sublabel: [e.employee_id, e.position || e.job_title || e.department].filter(Boolean).join(' · '),
    }));
}

const EMPTY: MyDelegations = { asDelegator: [], asDelegate: [] };
/** auth's ceiling for a coverage-only record (approval-delegation.service COVERAGE_ONLY_MAX_DAYS). */
const COVERAGE_ONLY_MAX_DAYS = 366;

// People, never ids: the API resolves names; an unresolvable person reads as a
// neutral label rather than a UUID.
function personLabel(name?: string | null): string {
  return (name ?? '').trim() || 'Colleague';
}

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

// Human label for a capability key from the catalog (falls back to the key).
const CAP_LABEL: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const cat of delegationCatalog()) for (const p of cat.permissions) m[p.key] = prettyCap(p.key, cat.label);
  return m;
})();
function prettyCap(key: string, categoryLabel: string): string {
  // "leave:approve" -> "Leave · Approve"; keep it readable without a hand map.
  const action = key.split(':').slice(1).join(' ').replace(/[_:]/g, ' ');
  const nice = action.replace(/\b\w/g, (c) => c.toUpperCase());
  return `${categoryLabel} · ${nice}`;
}

const SCOPE_LABELS: Record<DelegationScopeType, string> = {
  delegator_population: 'Everyone I can reach',
  department: 'Specific departments',
  branch: 'Specific branches',
  employees: 'Specific people',
};

const DelegationsPage: React.FC = () => {
  const canHold = usePermissionChecker();
  const isCompanyAdmin = useHasPermission('user:role:assign');

  const [data, setData] = useState<MyDelegations>(EMPTY);
  const [pending, setPending] = useState<Delegation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const promptText = usePrompt();
  // Deep link: notifications land on `…/admin/delegations?id=<delegationId>`.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get(DELEGATION_ID_PARAM);

  // create-form state
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<EmpHit[]>([]);
  const [delegate, setDelegate] = useState<EmpHit | null>(null);
  const [caps, setCaps] = useState<Record<string, boolean>>({});
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [scopeType, setScopeType] = useState<DelegationScopeType>('delegator_population');
  const [scopeRefs, setScopeRefs] = useState<string[]>([]);
  const [scopePeople, setScopePeople] = useState<EmpHit[]>([]); // display for 'employees'

  // Delegation ↔ Leave workflow: tenant policy (CC rules, buffers, confirmation),
  // the leave context we may have arrived with, CC recipients (visibility only),
  // and the confirmation card shown after a successful create.
  const location = useLocation();
  const navigate = useNavigate();
  const fromLeave = ((location.state ?? {}) as { fromLeave?: FromLeaveContext & { returnTo?: string } }).fromLeave;
  const [tenantPolicy, setTenantPolicy] = useState<DelegationPolicy | null>(null);
  const [ccPeople, setCcPeople] = useState<EmpHit[]>([]);
  const [ccQuery, setCcQuery] = useState('');
  const [ccHits, setCcHits] = useState<EmpHit[]>([]);
  const [created, setCreated] = useState<Delegation | null>(null);
  // ⚠️ QA L10 (2026-10-24): a REPLACEMENT for cover that was declined (or
  // cancelled) is for the same leave. Both ways in — "Choose another
  // delegate" on the declined row, and the leave's "create a new delegation"
  // link (?replaceFor=) — used to start a blank, unlinked delegation, so the
  // new row had no "Related absence" and the leave still had no cover.
  const [replacing, setReplacing] = useState<FromLeaveContext | null>(() => {
    const id = searchParams.get(REPLACE_FOR_PARAM);
    return id ? { leaveRequestId: id, startDate: searchParams.get('from') || undefined, endDate: searchParams.get('to') || undefined } : null;
  });
  const [deciding, setDeciding] = useState<string | null>(null);
  useEffect(() => { getDelegationPolicy().then(setTenantPolicy).catch(() => setTenantPolicy(null)); }, []);
  // The window this page filled in itself for ?replaceFor= (null = none yet).
  const replaceWindow = useRef<{ startsAt: string; endsAt: string } | null>(null);
  useEffect(() => {
    // Arrived from a leave that lost its cover: open the window on its dates.
    // ⚠️ DEF-0mvn4qf99001: the tenant policy loads AFTER the first fill, so this
    // re-runs when its buffers arrive and widens the window — but only while the
    // dates are still the ones filled here; dates the person changed stay theirs.
    if (fromLeave || !replacing?.startDate) return;
    // Once filled here, ANY difference is the person's — clearing both dates
    // included (DEF-0mvnbr7sg003). Before the first fill, only dates they typed.
    const auto = replaceWindow.current;
    if (auto ? auto.startsAt !== startsAt || auto.endsAt !== endsAt : startsAt || endsAt) return;
    const w = delegationWindowFromLeave(replacing.startDate, replacing.endDate || replacing.startDate, tenantPolicy?.dateBuffers);
    replaceWindow.current = w;
    setStartsAt(w.startsAt);
    setEndsAt(w.endsAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replacing?.startDate, replacing?.endDate, tenantPolicy?.dateBuffers?.startDaysBefore, tenantPolicy?.dateBuffers?.endDaysAfter]);
  useEffect(() => {
    if (!fromLeave?.startDate) return;
    const w = delegationWindowFromLeave(fromLeave.startDate, fromLeave.endDate || fromLeave.startDate, tenantPolicy?.dateBuffers);
    setStartsAt(w.startsAt);
    setEndsAt(w.endsAt);
    if (fromLeave.reason && !reason) setReason(fromLeave.reason);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLeave?.startDate, fromLeave?.endDate, tenantPolicy?.dateBuffers?.startDaysBefore, tenantPolicy?.dateBuffers?.endDaysAfter]);
  useEffect(() => {
    const q = ccQuery.trim();
    if (!q) { setCcHits([]); return; }
    const t = setTimeout(() => searchDelegates(q).then(setCcHits).catch(() => setCcHits([])), 250);
    return () => clearTimeout(t);
  }, [ccQuery]);
  const ccAllowed = tenantPolicy?.cc?.allowManual !== false;
  const ccRequired = tenantPolicy?.cc?.mode === 'required' && !tenantPolicy?.cc?.autoManager && !tenantPolicy?.cc?.autoHr;
  /** Leave the delegation flow WITHOUT creating one, back to the leave form.
   *  The draft is still in sessionStorage, so the form comes back as it was —
   *  abandoning coverage setup must not cost someone what they typed. */
  const backToLeaveUnchanged = () => {
    navigate(appRoute(location.pathname, 'time-off'), { state: { delegationCancelled: true } });
  };
  const goToLeave = (d: Delegation) => {
    navigate(appRoute(location.pathname, 'time-off'), {
      state: fromLeave?.returnTo
        ? { delegationCreated: { id: d.id, delegateName: d.delegateName ?? null, status: d.status, ccCount: d.ccUserIds?.length ?? 0 } }
        : { prefill: { startDate: String(d.startsAt).slice(0, 10), endDate: String(d.endsAt).slice(0, 10), delegationId: d.id, delegateName: d.delegateName ?? null } },
    });
  };
  const respondToDelegation = async (d: Delegation, accept: boolean) => {
    let declineReason: string | undefined;
    if (!accept) {
      const r = await promptText({
        title: 'Decline this delegation?',
        message: `${personLabel(d.delegatorName)} will be told and can choose someone else.`,
        label: 'Reason (optional)',
        multiline: true,
        confirmLabel: 'Decline',
        danger: true,
      });
      if (r == null) return;
      declineReason = r.trim() || undefined;
    }
    setDeciding(d.id);
    try {
      if (accept) await acceptDelegation(d.id);
      else await declineDelegation(d.id, declineReason);
      toast.success(accept ? 'Delegation accepted' : 'Delegation declined');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update delegation'));
    } finally {
      setDeciding(null);
    }
  };
  const chooseAnother = (d: Delegation) => {
    setDelegate(null);
    setCaps(Object.fromEntries((d.capabilities ?? []).map((c) => [c, true])));
    setStartsAt(String(d.startsAt).slice(0, 16));
    setEndsAt(String(d.endsAt).slice(0, 16));
    setNote(d.note ?? '');
    // The replacement covers the SAME leave the declined one did.
    setReplacing(d.leaveRequestId ? { leaveRequestId: d.leaveRequestId } : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // scope option lists
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
  // scope-people search
  const [scopeQuery, setScopeQuery] = useState('');
  const [scopeHits, setScopeHits] = useState<EmpHit[]>([]);

  // Catalog grouped by category, filtered to what the caller actually holds.
  const catalog = useMemo(() => {
    return delegationCatalog()
      .map((cat) => ({ ...cat, permissions: cat.permissions.filter((p) => canHold(p.key)) }))
      .filter((cat) => cat.permissions.length > 0);
    // canHold is derived from Redux; recompute when it changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/* canHold is stable per render set */ isCompanyAdmin]);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [mine, pend] = await Promise.all([
        getDelegations(),
        isCompanyAdmin ? getPendingDelegations() : Promise.resolve<Delegation[]>([]),
      ]);
      setData(mine);
      setPending(pend);
    } catch (e) {
      // A failed load is an ERROR state, never "no one has delegated to you".
      const msg = getApiErrorMessage(e, 'Could not load delegations');
      setLoadError(msg);
      toast.error(getApiErrorMessage(e, 'Could not load delegations'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompanyAdmin]);

  // Lazily load scope option lists the first time a narrowing scope is picked.
  useEffect(() => {
    if (scopeType === 'department' && departments.length === 0) {
      getDepartments()
        .then((d) => setDepartments((d.items || []).map((x: any) => ({ id: String(x.id), name: x.name || x.id }))))
        .catch(() => setDepartments([]));
    }
    if (scopeType === 'branch' && branches.length === 0) {
      listBranches()
        .then((b) => setBranches((b || []).map((x: any) => ({ id: String(x.id), name: x.name || x.id }))))
        .catch(() => setBranches([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeType]);

  // debounced delegate search
  useEffect(() => {
    if (delegate || query.trim().length < 1) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => searchDelegates(query).then(setHits).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [query, delegate]);

  // debounced scope-people search
  useEffect(() => {
    if (scopeType !== 'employees' || scopeQuery.trim().length < 1) {
      setScopeHits([]);
      return;
    }
    const t = setTimeout(() => searchDelegates(scopeQuery).then(setScopeHits).catch(() => setScopeHits([])), 250);
    return () => clearTimeout(t);
  }, [scopeQuery, scopeType]);

  const selectedCaps = useMemo(() => Object.keys(caps).filter((k) => caps[k]), [caps]);
  // Replacement cover for a leave may be COVERAGE-ONLY (no capabilities — the
  // delegate covers the work, no approvals delegated): auth allows that for an
  // origin=leave delegation. Requiring a capability here made a declined
  // manager-assigned cover (capabilities []) impossible to replace for anyone
  // who holds nothing delegable.
  const coverageOnly = !!replacing?.leaveRequestId && selectedCaps.length === 0;

  // Aggregate policy for the current selection (drives warnings + max window).
  const policy = useMemo(() => {
    let maxDays = Infinity;
    let sensitive = false;
    let reauth = false;
    let adminApproval = false;
    for (const c of selectedCaps) {
      const p = delegationPolicy(c);
      if (!p) continue;
      if (p.maxDurationDays < maxDays) maxDays = p.maxDurationDays;
      if (p.sensitivity === 'sensitive') sensitive = true;
      if (p.requiresReauthentication) reauth = true;
      if (p.requiresAdminApproval) adminApproval = true;
    }
    // Coverage-only grants nothing, so auth's own (longer) ceiling applies.
    const fallback = selectedCaps.length === 0 ? COVERAGE_ONLY_MAX_DAYS : 30;
    return { maxDays: Number.isFinite(maxDays) ? maxDays : fallback, sensitive, reauth, adminApproval };
  }, [selectedCaps]);

  const windowDays = useMemo(() => {
    if (!startsAt || !endsAt) return null;
    const d = (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 86_400_000;
    return Number.isFinite(d) ? d : null;
  }, [startsAt, endsAt]);
  const windowTooLong = windowDays != null && windowDays > policy.maxDays;
  const reasonRequired = policy.sensitive && !reason.trim();

  const scopeChosen =
    scopeType === 'delegator_population' || scopeRefs.length > 0;

  const canSubmit =
    !!delegate &&
    (selectedCaps.length > 0 || coverageOnly) &&
    !!startsAt &&
    !!endsAt &&
    !windowTooLong &&
    !reasonRequired &&
    scopeChosen &&
    !(ccRequired && ccPeople.length === 0) &&
    !saving;

  const resetForm = () => {
    setDelegate(null);
    setQuery('');
    setReason('');
    setNote('');
    setStartsAt('');
    setEndsAt('');
    setCaps({});
    setScopeType('delegator_population');
    setScopeRefs([]);
    setScopePeople([]);
    setScopeQuery('');
    setCcPeople([]);
    setCcQuery('');
  };

  // Step-up: capabilities flagged requiresReauthentication need a FRESH proof of
  // identity (2FA code / password) before the backend will create the
  // delegation. Ask up-front when we know it's needed, and also react to the
  // backend's REAUTH_REQUIRED refusal (proof expired mid-form) by prompting and
  // retrying — never a dead-end 403.
  const [reauthOpen, setReauthOpen] = useState(false);

  const submit = async () => {
    if (!delegate) return;
    if (!delegate.userId) {
      toast.error('That person has no user account yet, so they cannot receive a delegation.');
      return;
    }
    if (policy.reauth) {
      const st = await getReauthStatus().catch(() => null);
      if (!st?.reauthenticated) {
        setReauthOpen(true);
        return;
      }
    }
    await doCreate();
  };

  const doCreate = async () => {
    if (!delegate?.userId) return;
    setSaving(true);
    try {
      const madeRow = await createDelegation({
        delegateEmployeeId: delegate.employeeId,
        delegateUserId: delegate.userId,
        capabilities: selectedCaps,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        reason: reason.trim() || undefined,
        note: note.trim() || undefined,
        scopeType,
        scopeRefs: scopeType === 'delegator_population' ? [] : scopeRefs,
        ccUserIds: ccPeople.map((p) => p.userId).filter((x): x is string => !!x),
        leaveRequestId: fromLeave?.leaveRequestId ?? replacing?.leaveRequestId ?? null,
        origin: fromLeave || replacing ? 'leave' : 'manual',
      });
      // Point the leave at its new cover (the leave still names the declined
      // one). Validated server-side like submission; a refusal is shown in
      // the server's words — the delegation itself was created.
      if (!fromLeave && replacing?.leaveRequestId) {
        try {
          await replaceLeaveDelegation(replacing.leaveRequestId, madeRow.id);
        } catch (e) {
          toast.error(getApiErrorMessage(e, "The delegation was created, but couldn't be attached to your time off. Open the request and choose it there."), { duration: 8000 });
        }
        clearReplacing();
      }
      toast.success(
        madeRow.status === 'pending_approval'
          ? 'Delegation submitted for approval'
          : madeRow.status === 'pending_acceptance'
            ? `Delegation sent to ${madeRow.delegateName || delegate.name} to accept`
            : 'Delegation created',
      );
      setCreated({ ...madeRow, delegateName: madeRow.delegateName ?? delegate.name });
      resetForm();
      await load();
      if (fromLeave?.returnTo) goToLeave({ ...madeRow, delegateName: madeRow.delegateName ?? delegate.name });
    } catch (e) {
      if (isReauthRequiredError(e)) {
        setReauthOpen(true);
        return;
      }
      toast.error(getApiErrorMessage(e, 'Could not create delegation'));
    } finally {
      setSaving(false);
    }
  };

  const clearReplacing = () => {
    setReplacing(null);
    if (searchParams.has(REPLACE_FOR_PARAM)) {
      const next = new URLSearchParams(searchParams);
      [REPLACE_FOR_PARAM, 'from', 'to'].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  };

  const revoke = async (id: string) => {
    try {
      await revokeDelegation(id);
      toast.success('Delegation revoked');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not revoke delegation'));
    }
  };
  const decide = async (id: string, approve: boolean) => {
    try {
      await (approve ? approveDelegation(id) : rejectDelegation(id));
      toast.success(approve ? 'Delegation approved' : 'Delegation rejected');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update delegation'));
    }
  };

  const capLabels = (keys: string[]) => keys.map((k) => CAP_LABEL[k] || k).join(', ');

  // NE-17 self-cover: the viewer arranged this cover AND is its delegate.
  // auth lists it in both; arrangedByMe is the proof they may edit their note.
  const arrangedIds = useMemo(() => new Set((data.arrangedByMe ?? []).map((d) => d.id)), [data]);
  const coverIds = useMemo(() => new Set(data.asDelegate.map((d) => d.id)), [data]);

  // The delegation a deep link points at, from whichever list holds it.
  const focused = useMemo(() => {
    if (!focusId) return null;
    const asDelegate = data.asDelegate.find((d) => d.id === focusId);
    if (asDelegate) return { d: asDelegate, viewerIsDelegate: true, viewerIsArranger: arrangedIds.has(focusId) };
    const own = data.asDelegator.find((d) => d.id === focusId) ?? pending.find((d) => d.id === focusId);
    if (own) return { d: own, viewerIsDelegate: false, viewerIsArranger: false };
    // Cover this manager ARRANGED (their notices deep-link here): their own
    // note only — auth sends no more than that in arrangedByMe.
    const arranged = (data.arrangedByMe ?? []).find((d) => d.id === focusId);
    return arranged ? { d: arranged, viewerIsDelegate: false, viewerIsArranger: true } : null;
  }, [focusId, data, pending, arrangedIds]);
  // HO-0mvoms259001-eee863: when auth couldn't check the cover this person
  // arranged, a link to it isn't "not available" — it is unknown right now.
  const arrangedUnchecked = data.arrangedByMeAvailable === false;
  const detailState: DetailState = loading ? 'loading' : loadError ? 'error' : focused ? 'ready'
    : arrangedUnchecked ? 'error' : 'missing';
  const detailError = loadError ?? (arrangedUnchecked
    ? "We couldn't check the cover you've arranged right now, so this can't be shown. Try again in a moment."
    : null);
  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(DELEGATION_ID_PARAM);
    setSearchParams(next, { replace: true });
  };
  const openDetail = (d: Delegation) => {
    const next = new URLSearchParams(searchParams);
    next.set(DELEGATION_ID_PARAM, d.id);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    if (!focusId || loading) return;
    document.getElementById(`delegation-${focusId}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [focusId, loading]);
  const viewLeave = (leaveRequestId: string) =>
    navigate(appRoute(location.pathname, 'time-off'), { state: { openRequestId: leaveRequestId } });
  const inbox = (
    <DelegateInbox
      rows={data.asDelegate}
      loading={loading}
      error={loadError}
      onRetry={() => void load()}
      capLabels={capLabels}
      decidingId={deciding}
      focusedId={focusId}
      onRespond={(d, accept) => void respondToDelegation(d, accept)}
      onOpen={openDetail}
    />
  );
  // The delegate's inbox leads the page when there is something in it (or
  // when we could not tell); an empty one sits with the other lists.
  const inboxFirst = loading || !!loadError || data.asDelegate.length > 0;
  const toggleRef = (id: string) =>
    setScopeRefs((r) => (r.includes(id) ? r.filter((x) => x !== id) : [...r, id]));

  const scopeSummary = (d: Delegation): string => {
    const t = (d.scopeType || 'delegator_population') as DelegationScopeType;
    if (t === 'delegator_population') return SCOPE_LABELS.delegator_population;
    const n = d.scopeRefs?.length ?? 0;
    return `${SCOPE_LABELS[t]} (${n})`;
  };

  // The same name for a status everywhere — the drawer, the cards and here
  // (NE-21: this badge printed the raw status, "Pending acceptance", next to a
  // drawer saying "Awaiting acceptance").
  const statusBadge = (d: Delegation) => {
    const s = d.status || (d.revokedAt ? 'revoked' : d.active ? 'active' : 'scheduled');
    const variant = s === 'active' ? undefined : 'secondary';
    return <Badge variant={variant as any}>{delegationStatusLabel(d)}</Badge>;
  };

  // Cover the viewer ARRANGED for a report: they're CC (not a party), so the
  // row shows and edits THEIR note only — never the employee's.
  const renderArrangedRow = (d: Delegation) => (
    <tr key={d.id} id={`delegation-${d.id}`} className="border-t border-border align-top" data-testid={`arranged-${d.id}`}>
      <td className="px-4 py-3 text-charcoal">{personLabel(d.delegatorName)}</td>
      <td className="px-4 py-3 text-muted-foreground">
        <div>Covered by {coverIds.has(d.id) ? 'you' : personLabel(d.delegateName)}</div>
        <DelegationNotes className="mt-1.5" delegation={d} viewer="manager" onSaved={() => void load()} />
      </td>
      <td className="px-4 py-3 text-muted-foreground text-sm">
        {fmt(d.startsAt)} → {fmt(d.endsAt)}
      </td>
      <td className="px-4 py-3">{statusBadge(d)}</td>
      <td className="px-4 py-3" />
    </tr>
  );

  const renderRow = (d: Delegation, mine: boolean) => (
    <tr key={d.id} id={`delegation-${d.id}`} className={`border-t border-border align-top ${focusId === d.id ? 'bg-muted/40' : ''}`}>
      <td className="px-4 py-3 text-charcoal">{mine ? personLabel(d.delegateName) : personLabel(d.delegatorName)}</td>
      <td className="px-4 py-3 text-muted-foreground">
        <div>{capLabels(d.capabilities)}</div>
        <div className="text-xs mt-0.5 flex items-center gap-2">
          <span>{scopeSummary(d)}</span>
          {d.sensitivity === 'sensitive' && (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              <ShieldAlert className="w-3 h-3" /> Sensitive
            </span>
          )}
        </div>
        <DelegationNotes className="mt-1.5" delegation={d} viewer={mine ? 'employee' : 'delegate'} onSaved={() => void load()} />
        {d.leaveRequestId && (
          <div className="mt-1.5 text-xs">
            <span className="text-muted-foreground">Related absence · </span>
            <button className="text-primary-500 hover:underline" onClick={() => viewLeave(d.leaveRequestId!)}>
              View leave request
            </button>
          </div>
        )}
        {(d.ccUserIds?.length ?? 0) > 0 && <div className="mt-1 text-xs text-muted-foreground">CC {d.ccUserIds!.length} (visibility only)</div>}
        {mine && d.status === 'declined' && (
          <div className="mt-1.5 text-xs">
            <span className="text-muted-foreground">{personLabel(d.delegateName)} declined{d.declineReason ? `: ${d.declineReason}` : ''}. </span>
            <button className="text-primary-500 hover:underline" onClick={() => chooseAnother(d)}>Choose another delegate</button>
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground text-sm">
        {fmt(d.startsAt)} → {fmt(d.endsAt)}
      </td>
      <td className="px-4 py-3">{statusBadge(d)}</td>
      <td className="px-4 py-3 text-right">
        {mine && !d.revokedAt && d.status !== 'revoked' && d.status !== 'rejected' && (
          <Button variant="ghost" size="sm" onClick={() => revoke(d.id)}>
            Revoke
          </Button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-8">
      <ReauthDialog
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onVerified={() => {
          setReauthOpen(false);
          void doCreate();
        }}
        description="This delegation includes sensitive capabilities, so please confirm your identity before it is created."
      />
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
          <UserCheck className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">Delegation</h1>
          <p className="text-muted-foreground mt-1">
            Going away? Let someone act on your behalf for a while. They get exactly the capabilities,
            the people, and the dates you choose — never more than you hold yourself.
          </p>
        </div>
      </div>

      {/* Approval queue (company admins) */}
      {isCompanyAdmin && pending.length > 0 && (
        <div className="rounded-[14px] border border-amber-300/60 bg-amber-50/60 dark:bg-amber-500/[0.06] p-5 space-y-3">
          <h2 className="text-charcoal font-medium flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" /> Awaiting your approval
          </h2>
          <div className="space-y-2">
            {pending.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="text-charcoal">
                  {personLabel(d.delegatorName)} → {personLabel(d.delegateName)}
                  <span className="text-muted-foreground"> · {capLabels(d.capabilities)} · {fmt(d.startsAt)}–{fmt(d.endsAt)}</span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => decide(d.id, true)}>Approve</Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(d.id, false)}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation card → Continue to Leave Request (Delegation → Leave) */}
      {created && (
        <div className="rounded-[14px] border border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-500/[0.06] p-5 max-w-2xl space-y-3">
          <div className="flex items-center gap-2 font-medium text-charcoal">
            <CalendarCheck className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
            {created.status === 'pending_acceptance' ? 'Delegation sent for acceptance' : created.status === 'pending_approval' ? 'Delegation submitted for approval' : 'Delegation confirmed'}
          </div>
          <div className="text-sm text-charcoal">
            {personLabel(created.delegateName)} · {fmt(String(created.startsAt))} – {fmt(String(created.endsAt))}
            {(created.ccUserIds?.length ?? 0) > 0 && <span className="text-muted-foreground"> · CC {created.ccUserIds!.length}</span>}
          </div>
          {created.status === 'pending_acceptance' && (
            <div className="text-xs text-muted-foreground">{personLabel(created.delegateName)} must accept before it takes effect. You'll be notified.</div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => goToLeave(created)}>
              Continue to Leave Request <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>Done</Button>
          </div>
        </div>
      )}

      {/* The delegate's inbox: awaiting acceptance / upcoming / active / past */}
      {inboxFirst && inbox}

      <DelegationDetailSheet
        open={!!focusId}
        onClose={closeDetail}
        state={detailState}
        error={detailError}
        onRetry={() => void load()}
        delegation={focused?.d ?? null}
        viewerIsDelegate={focused?.viewerIsDelegate ?? false}
        viewerIsArranger={focused?.viewerIsArranger ?? false}
        capLabels={capLabels}
        deciding={!!focused && deciding === focused.d.id}
        onRespond={(d, accept) => void respondToDelegation(d, accept)}
        onViewLeave={viewLeave}
      />

      {/* Create */}
      <div className="rounded-[14px] border border-border p-5 space-y-5 max-w-2xl">
        <h2 className="text-charcoal font-medium">Delegate my authority</h2>
        {fromLeave?.startDate && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] bg-muted/40 p-3 text-sm text-muted-foreground">
            <span>
              Setting up coverage for your time off <span className="text-charcoal">{fromLeave.startDate}{fromLeave.endDate && fromLeave.endDate !== fromLeave.startDate ? ` – ${fromLeave.endDate}` : ''}</span>. We'll bring you back to the leave request when it's done.
            </span>
            <button
              type="button"
              onClick={backToLeaveUnchanged}
              className="shrink-0 text-xs font-semibold text-charcoal underline underline-offset-2"
            >
              Back to the leave request
            </button>
          </div>
        )}

        {!fromLeave && replacing && (
          <div data-testid="replacing-for-leave" className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] bg-muted/40 p-3 text-sm text-muted-foreground">
            <span>
              Choosing a new delegate for your time off{replacing.startDate ? <> <span className="text-charcoal">{replacing.startDate}{replacing.endDate && replacing.endDate !== replacing.startDate ? ` – ${replacing.endDate}` : ''}</span></> : ''}. The new delegation will cover that request.
            </span>
            <button type="button" onClick={clearReplacing} className="shrink-0 text-xs font-semibold text-charcoal underline underline-offset-2">
              Not for that leave
            </button>
          </div>
        )}

        {/* Delegate */}
        <div className="relative">
          <label className="text-sm text-muted-foreground">To</label>
          {delegate ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-charcoal">{delegate.name}</span>
              {delegate.sublabel && <span className="text-xs text-muted-foreground">{delegate.sublabel}</span>}
              <button className="text-muted-foreground hover:text-charcoal" onClick={() => setDelegate(null)} aria-label="Clear">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <input
                className="mt-1 w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal"
                placeholder="Search a colleague by name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {hits.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-[10px] border border-border bg-background shadow-lg max-h-60 overflow-auto">
                  {hits.map((h) => (
                    <button
                      key={h.employeeId}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50"
                      onClick={() => {
                        setDelegate(h);
                        setHits([]);
                      }}
                    >
                      <div className="text-charcoal">{h.name}</div>
                      {h.sublabel && <div className="text-xs text-muted-foreground">{h.sublabel}</div>}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Capabilities — grouped, metadata-driven, filtered to what you hold */}
        <div>
          <label className="text-sm text-muted-foreground">Capabilities{replacing?.leaveRequestId ? ' (optional for cover)' : ''}</label>
          {replacing?.leaveRequestId && catalog.length > 0 && (
            <div className="mt-1 text-xs text-muted-foreground" data-testid="coverage-only-hint">
              Leave these unticked and they only cover your work — no approvals are delegated.
            </div>
          )}
          {catalog.length === 0 ? (
            <div className="mt-1 text-sm text-muted-foreground">
              {replacing?.leaveRequestId
                ? "You don't hold any capabilities that can be delegated — that's fine for cover."
                : "You don't hold any capabilities that can be delegated."}
            </div>
          ) : (
            <div className="mt-2 space-y-4 max-h-72 overflow-auto pr-1">
              {catalog.map((cat) => (
                <div key={cat.category}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-2">
                    {cat.label}
                    {cat.sensitivity === 'sensitive' && (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 normal-case tracking-normal">
                        <ShieldAlert className="w-3 h-3" /> sensitive
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {cat.permissions.map((p) => (
                      <label key={p.key} className="inline-flex items-center gap-2 text-charcoal text-sm">
                        <input
                          type="checkbox"
                          checked={!!caps[p.key]}
                          onChange={(e) => setCaps((prev) => ({ ...prev, [p.key]: e.target.checked }))}
                        />
                        {prettyCap(p.key, '').replace(/^ · /, '')}
                        {p.sensitivity === 'sensitive' && <ShieldAlert className="w-3 h-3 text-amber-600" />}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scope — the delegated population */}
        <div>
          <label className="text-sm text-muted-foreground">Who they can act on</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {(Object.keys(SCOPE_LABELS) as DelegationScopeType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setScopeType(t);
                  setScopeRefs([]);
                  setScopePeople([]);
                }}
                className={`px-3 py-1.5 rounded-[10px] border text-sm ${
                  scopeType === t ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.1] text-charcoal' : 'border-border text-muted-foreground'
                }`}
              >
                {SCOPE_LABELS[t]}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
            <Info className="w-3 h-3" /> Whatever you pick is still limited to the people you can reach yourself.
          </p>

          {scopeType === 'department' && (
            <div className="mt-2 flex flex-wrap gap-2">
              {departments.map((d) => (
                <label key={d.id} className={`px-2.5 py-1 rounded-[8px] border text-sm cursor-pointer ${scopeRefs.includes(d.id) ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.1]' : 'border-border'}`}>
                  <input type="checkbox" className="mr-1.5" checked={scopeRefs.includes(d.id)} onChange={() => toggleRef(d.id)} />
                  {d.name}
                </label>
              ))}
              {departments.length === 0 && <span className="text-xs text-muted-foreground">Loading departments…</span>}
            </div>
          )}
          {scopeType === 'branch' && (
            <div className="mt-2 flex flex-wrap gap-2">
              {branches.map((b) => (
                <label key={b.id} className={`px-2.5 py-1 rounded-[8px] border text-sm cursor-pointer ${scopeRefs.includes(b.id) ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.1]' : 'border-border'}`}>
                  <input type="checkbox" className="mr-1.5" checked={scopeRefs.includes(b.id)} onChange={() => toggleRef(b.id)} />
                  {b.name}
                </label>
              ))}
              {branches.length === 0 && <span className="text-xs text-muted-foreground">Loading branches…</span>}
            </div>
          )}
          {scopeType === 'employees' && (
            <div className="mt-2 space-y-2">
              {scopePeople.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {scopePeople.map((p) => (
                    <span key={p.employeeId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[8px] border border-border text-sm">
                      {p.name}
                      <button
                        aria-label="Remove"
                        onClick={() => {
                          setScopePeople((s) => s.filter((x) => x.employeeId !== p.employeeId));
                          setScopeRefs((r) => r.filter((x) => x !== p.employeeId));
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  className="w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal text-sm"
                  placeholder="Add a person…"
                  value={scopeQuery}
                  onChange={(e) => setScopeQuery(e.target.value)}
                />
                {scopeHits.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-[10px] border border-border bg-background shadow-lg max-h-60 overflow-auto">
                    {scopeHits
                      .filter((h) => !scopeRefs.includes(h.employeeId))
                      .map((h) => (
                        <button
                          key={h.employeeId}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50"
                          onClick={() => {
                            setScopePeople((s) => [...s, h]);
                            setScopeRefs((r) => [...r, h.employeeId]);
                            setScopeQuery('');
                            setScopeHits([]);
                          }}
                        >
                          <div className="text-charcoal text-sm">{h.name}</div>
                          {h.sublabel && <div className="text-xs text-muted-foreground">{h.sublabel}</div>}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Window */}
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="text-sm text-muted-foreground">From</label>
            <DateTimeField className="mt-1" value={startsAt} onChange={setStartsAt} />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Until</label>
            <DateTimeField className="mt-1" value={endsAt} onChange={setEndsAt} defaultTime="17:00" />
          </div>
        </div>
        {(selectedCaps.length > 0 || coverageOnly) && (
          <p className={`text-xs flex items-center gap-1 ${windowTooLong ? 'text-red-600' : 'text-muted-foreground'}`}>
            <Clock className="w-3 h-3" />
            {windowTooLong
              ? `This selection allows at most ${policy.maxDays} days.`
              : `Maximum window for this selection: ${policy.maxDays} days.`}
          </p>
        )}

        {/* Reason */}
        <div>
          <label className="text-sm text-muted-foreground">
            Reason {policy.sensitive ? <span className="text-red-600">(required)</span> : '(optional)'}
          </label>
          <input
            className="mt-1 w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal"
            placeholder={policy.sensitive ? 'Why is this needed? (recorded on the audit trail)' : 'e.g. Annual leave'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {/* Handover note — for the delegate, so the handover needs no side-channel message */}
        <div>
          <label className="text-sm text-muted-foreground">
            Note to {delegate?.name ?? 'the delegate'} <span>(optional)</span>
          </label>
          <textarea
            className="mt-1 w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal min-h-[84px]"
            placeholder="What should they focus on, and where do things stand? e.g. “Please prioritise the Q3 audit approvals — they're urgent. Everything else can wait until I'm back.”"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="mt-1 text-xs text-muted-foreground">
            {delegate?.name ?? 'The delegate'} will see this in the app and in their delegation notification.
          </div>
        </div>

        {/* CC recipients — visibility & notifications ONLY, never authority */}
        <div>
          <label className="text-sm text-muted-foreground">
            CC {tenantPolicy?.cc?.mode === 'required' ? <span className="text-red-600">(required)</span> : '(optional)'}
          </label>
          <div className="mt-1 text-xs text-muted-foreground">Receives visibility and notifications only — no delegated authority.</div>
          {(tenantPolicy?.cc?.autoManager || tenantPolicy?.cc?.autoHr) && (
            <div className="mt-1 text-xs text-muted-foreground">
              {tenantPolicy?.cc?.autoManager && 'Your manager is copied automatically by company policy. '}
              {tenantPolicy?.cc?.autoHr && 'HR is copied automatically by company policy.'}
            </div>
          )}
          {ccPeople.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {ccPeople.map((p) => (
                <Badge key={p.employeeId} variant="secondary" className="gap-1">
                  {p.name}
                  <button className="ml-1 text-muted-foreground hover:text-charcoal" onClick={() => setCcPeople((l) => l.filter((x) => x.employeeId !== p.employeeId))} aria-label={`Remove ${p.name} from CC`}>×</button>
                </Badge>
              ))}
            </div>
          )}
          {ccAllowed && (
            <div className="relative">
              <input
                className="mt-2 w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal"
                placeholder="Add someone to copy (e.g. your manager)…"
                value={ccQuery}
                onChange={(e) => setCcQuery(e.target.value)}
              />
              {ccHits.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full rounded-[10px] border border-border bg-background shadow-md max-h-56 overflow-auto">
                  {ccHits.filter((h) => !!h.userId && !ccPeople.some((p) => p.employeeId === h.employeeId)).map((h) => (
                    <li key={h.employeeId}>
                      <button type="button" className="w-full text-left px-3 py-2 hover:bg-muted/60 text-sm" onClick={() => { setCcPeople((l) => [...l, h]); setCcQuery(''); setCcHits([]); }}>
                        <span className="text-charcoal">{h.name}</span>
                        {h.sublabel && <span className="text-xs text-muted-foreground"> · {h.sublabel}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Sensitive warnings */}
        {policy.sensitive && (
          <div className="rounded-[10px] border border-amber-300/60 bg-amber-50/60 dark:bg-amber-500/[0.06] p-3 text-sm text-charcoal space-y-1">
            <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
              <ShieldAlert className="w-4 h-4" /> Sensitive delegation
            </div>
            <ul className="list-disc ml-5 text-muted-foreground space-y-0.5">
              <li>This grants access to sensitive operations. Keep the window short.</li>
              {policy.reauth && <li>You will be asked to confirm your identity (two-factor code or password) before this is created.</li>}
              {policy.adminApproval && <li>A Company Admin must approve it before it takes effect.</li>}
              <li>Every action taken under it is recorded on the immutable audit trail.</li>
            </ul>
          </div>
        )}

        {/* Review summary */}
        {delegate && selectedCaps.length > 0 && (
          <div className="rounded-[10px] bg-muted/40 p-3 text-sm text-muted-foreground">
            <span className="text-charcoal">{delegate.name}</span> will be able to{' '}
            <span className="text-charcoal">{capLabels(selectedCaps)}</span> for{' '}
            <span className="text-charcoal">{SCOPE_LABELS[scopeType].toLowerCase()}</span>
            {scopeType !== 'delegator_population' && ` (${scopeRefs.length} selected)`}
            {startsAt && endsAt && (
              <>
                {' '}from <span className="text-charcoal">{fmt(startsAt)}</span> to{' '}
                <span className="text-charcoal">{fmt(endsAt)}</span>
              </>
            )}
            .
          </div>
        )}

        {delegate && coverageOnly && (
          <div className="rounded-[10px] bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="coverage-only-summary">
            <span className="text-charcoal">{delegate.name}</span> will cover your time off
            {startsAt && endsAt && (
              <>
                {' '}from <span className="text-charcoal">{fmt(startsAt)}</span> to{' '}
                <span className="text-charcoal">{fmt(endsAt)}</span>
              </>
            )}
            . Cover only — no approvals are delegated.
          </div>
        )}

        <Button onClick={submit} disabled={!canSubmit}>
          {saving ? 'Submitting…' : policy.adminApproval ? 'Submit for approval' : 'Create delegation'}
        </Button>
      </div>

      {/* Lists */}
      {loading ? (
        <DataState variant="loading" compact message="Loading your delegations…" />
      ) : loadError ? (
        <DataState variant="error" title="Couldn't load delegations you've granted" message={loadError} onRetry={() => void load()} />
      ) : (
        <DelegationTable
          title="Delegations I've granted"
          firstCol="Delegate"
          rows={data.asDelegator}
          render={(d) => renderRow(d, true)}
          empty="You haven't delegated anything."
        />
      )}
      {arrangedUnchecked && (
        <div className="space-y-2" data-testid="arranged-cover-unchecked">
          <h2 className="text-sm font-semibold text-charcoal">Cover you&apos;ve arranged</h2>
          <DataState
            variant="error"
            title="Couldn't check the cover you've arranged"
            message="We couldn't confirm which of it you can still see right now. It hasn't been removed. Try again in a moment."
            onRetry={() => void load()}
          />
        </div>
      )}
      {(data.arrangedByMe?.length ?? 0) > 0 && (
        <DelegationTable
          title="Cover you've arranged"
          firstCol="Covering for"
          rows={data.arrangedByMe ?? []}
          render={renderArrangedRow}
          empty=""
        />
      )}
      {!inboxFirst && inbox}
    </div>
  );
};

const DelegationTable: React.FC<{
  title: string;
  firstCol: string;
  rows: Delegation[];
  render: (d: Delegation) => React.ReactNode;
  empty: string;
}> = ({ title, firstCol, rows, render, empty }) => (
  <div className="space-y-2">
    <h2 className="text-charcoal font-medium">{title}</h2>
    {rows.length === 0 ? (
      <div className="text-muted-foreground text-sm">{empty}</div>
    ) : (
      <div className="rounded-[14px] border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-4 py-3">{firstCol}</th>
              <th className="text-left font-medium px-4 py-3">For</th>
              <th className="text-left font-medium px-4 py-3">Window</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>{rows.map(render)}</tbody>
        </table>
      </div>
    )}
  </div>
);

export default DelegationsPage;
